import type { QueryClient, UseMutationResult } from "@tanstack/react-query";

import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  useBulkCreateSongs,
  useCreateSong,
  useUpdateSong,
} from "@/api/mutations";

import type {
  ApiResponse,
  BulkCreateResponse,
  CreateSongData,
  SongFeature,
  SongOperationsOptions,
  SongOperationsResult,
  UpdateSongData,
} from "./types";

import { formatUpdateWorkForApi, formatWorkForApi } from "../utils/index";

// Define mutation hooks as proper types with generics
type BulkCreateSongsMutationHook = UseMutationResult<
  BulkCreateResponse,
  Error,
  Record<string, unknown>,
  unknown
>;

type CreateSongMutationHook = UseMutationResult<
  ApiResponse,
  Error,
  Record<string, unknown>,
  unknown
>;

type UpdateSongMutationHook = UseMutationResult<
  ApiResponse,
  Error,
  Record<string, unknown>,
  unknown
>;

// Helper function for invalidating cache

function invalidateCaches(queryClient: QueryClient): void {
  // Invalidate the songs list cache to refresh the data
  void queryClient.invalidateQueries({
    queryKey: ["getMySongsDetailedPaginated"],
    exact: false,
  });

  // Also invalidate individual song details cache
  void queryClient.invalidateQueries({
    queryKey: ["getSong"],
    exact: false,
  });

  // Also invalidate recordings cache in case recordings were updated
  void queryClient.invalidateQueries({
    queryKey: ["getRecordingsDetails"],
    exact: false,
  });
}

/**
 * Custom hook for managing song operations like create and update
 */

export function useSongOperations({
  selectedAccount,
  onSuccess = () => {},
  onValidationError = () => {},
  onError = () => {},
}: SongOperationsOptions): SongOperationsResult {
  const queryClient = useQueryClient();

  // Cast the mutation hooks to their proper types

  const createMutation = useCreateSong() as unknown as CreateSongMutationHook;
  const bulkCreateMutation = useBulkCreateSongs() as unknown as BulkCreateSongsMutationHook;
  const updateMutation = useUpdateSong() as unknown as UpdateSongMutationHook;

  const operations = useMemo(() => ({
    /**
     * Single song uses /api/v1/work/create
     * Multiple songs uses /api/v1/work/bulkCreate
     */
    createSongs: async (songData: CreateSongData | CreateSongData[], feature?: SongFeature): Promise<ApiResponse | BulkCreateResponse> => {
      let isBulk: boolean;

      let mutation: BulkCreateSongsMutationHook | CreateSongMutationHook;

      if (Array.isArray(songData)) {
        isBulk = true;
        mutation = bulkCreateMutation;
      }
      else {
        isBulk = false;
        mutation = createMutation;
      }

      // Reset the mutation state if it exists
      if (typeof mutation.reset === "function") {
        mutation.reset();
      }

      const resolvedFeature = feature ?? (isBulk ? "AddAlbum" : "AddSong");

      const data = isBulk
        ? { feature: resolvedFeature, works: (songData as CreateSongData[]).map(song => formatWorkForApi(song, selectedAccount)) }
        : { ...formatWorkForApi(songData as CreateSongData, selectedAccount), feature: resolvedFeature };

      return new Promise<ApiResponse | BulkCreateResponse>((resolve, reject) => {
        mutation.mutate(data, {
          onSuccess: (response: ApiResponse | BulkCreateResponse) => {
            // Handle bulk response first
            if (isBulk && "failurecount" in response) {
              const bulkResponse = response;

              if (bulkResponse.failurecount > 0) {
                const failedResults = bulkResponse.results?.filter(r => r.failedvalidation) ?? [];

                const allMessages = failedResults.flatMap(r => r.messages ?? []);

                onValidationError(allMessages.length > 0 ? allMessages : (bulkResponse.messages ?? []));
                reject(new Error("One or more songs failed validation"));
                // Exit early back to client
                return;
              }
            }

            // Handle single song response
            else if ("failedvalidation" in response && response.failedvalidation === true) {
              const fallbackMessage = "Validation failed";
              const messages = response.messages && response.messages.length > 0
                ? response.messages
                : [fallbackMessage];
              onValidationError(messages);
              reject(new Error(messages.join(", ")));
              return;
            }

            invalidateCaches(queryClient);

            onSuccess(response);
            resolve(response);
          },
          onError: (error: Error) => {
            onError(error?.message || "Unknown error");
            reject(error);
          },
        });
      });
    },

    /**
     * Update an existing song
     */

    updateSong: async (songData: UpdateSongData): Promise<ApiResponse> => {
      // Reset mutation state if it exists
      if (typeof updateMutation.reset === "function") {
        updateMutation.reset();
      }

      const data = formatUpdateWorkForApi(songData, selectedAccount);

      return new Promise<ApiResponse>((resolve, reject) => {
        updateMutation.mutate(data, {
          onSuccess: (response: ApiResponse) => {
            if (response?.failedvalidation) {
              const fallbackMessage = "Validation failed";
              const messages = response.messages && response.messages.length > 0
                ? response.messages
                : [fallbackMessage];
              onValidationError(messages);
              reject(new Error(messages.join(", ")));
            }
            else {
              invalidateCaches(queryClient);

              onSuccess(response);
              resolve(response);
            }
          },
          onError: (error: Error) => {
            onError(error?.message || "Unknown error");
            reject(error);
          },
        });
      });
    },
  }), [bulkCreateMutation, createMutation, updateMutation, selectedAccount, onSuccess, onValidationError, onError, queryClient]);

  // Return operations and loading states (isPending is the TanStack Query v5 equivalent of isLoading)

  const isCreating = Boolean(createMutation?.isPending) || Boolean(bulkCreateMutation?.isPending);
  const isUpdating = Boolean(updateMutation?.isPending);
  const isLoading = isCreating || isUpdating;

  return {
    ...operations,
    isCreating,
    isUpdating,
    isLoading,
  };
}
